/**
 * Opening a map.
 *
 * This is the answer to "the map is still there after closing the browser" — MVP acceptance
 * criteria 1 and 2 — and to 14 and 15, which say a crash must not corrupt the map and that
 * recovery must be *offered* rather than silently performed.
 *
 * The sequence is: read the newest checkpoint, replay everything journalled after it, then look
 * hard at what came out. Three outcomes are possible, all three are reported rather than thrown,
 * and the distinction between the last two is the one that matters to a person:
 *
 *   - `clean`      — the document is the one they left
 *   - `incomplete` — the document is valid, but some entries did not make it back: unreadable on
 *                    disk, or refused by replay. Edits were lost, and saying so is the difference
 *                    between a bug report and a betrayal
 *   - `damaged`    — the document fails its own invariants. Nothing is overwritten and the last
 *                    intact checkpoint is offered as a choice
 *
 * What this never does is repair. Guessing at a damaged document is how a subtly wrong map gets
 * written back over a recoverable one.
 */
import { checkIntegrity, isIntact, } from '@orbit-mind/domain';
import { replay } from '@orbit-mind/editor-commands';
import { err, ok, } from '@orbit-mind/shared-types';
import { journalError, storageFailure } from './errors';
import { createJournalStore, toCheckpoint, toEntry, } from './store';
/**
 * The verdict on a restored document — pure, so the decision can be tested without contriving a
 * damaged disk, and so the same rules apply wherever a document is read back later.
 */
export function assessRestoration(input) {
    const integrity = checkIntegrity(input.document);
    if (integrity.length === 0) {
        return {
            // Both count the same way. From the user's side there is no difference between an edit
            // that could not be read and one that could not be applied: it is not in their map.
            outcome: input.skipped + input.unreadable > 0 ? 'incomplete' : 'clean',
            integrity,
            fallback: null,
            recoveryAvailable: input.skipped + input.unreadable > 0,
        };
    }
    // Offered only when it would actually help. A checkpoint damaged in the same way is not a
    // recovery, and presenting it as one costs the user their remaining edits for nothing.
    const fallback = input.checkpointDocument !== null && isIntact(input.checkpointDocument)
        ? input.checkpointDocument
        : null;
    return { outcome: 'damaged', integrity, fallback, recoveryAvailable: true };
}
/** Damaged rows from both reads, counted once each. */
function mergeDamaged(...reads) {
    const bySequence = new Map();
    const unnumbered = [];
    for (const read of reads) {
        for (const damaged of read) {
            // A row too damaged to yield a sequence cannot be recognised as one already seen, so it is
            // kept as its own. Over-reporting a corrupt row is the safe direction to be wrong in.
            if (damaged.sequence === null)
                unnumbered.push(damaged);
            else if (!bySequence.has(damaged.sequence))
                bySequence.set(damaged.sequence, damaged);
        }
    }
    return [...bySequence.values(), ...unnumbered];
}
/** Did everything the user did come back? */
export function isHealthy(opened) {
    return opened.outcome === 'clean';
}
export async function openJournal(options) {
    const { storage, mapId } = options;
    let checkpoint;
    let entries;
    let pendingRecords;
    let unreadable;
    try {
        checkpoint = await storage.readCheckpoint(mapId);
        const journal = checkpoint === undefined
            ? await storage.readEntries(mapId)
            : await storage.readEntries(mapId, checkpoint.sequence);
        const outbox = await storage.readPending(mapId);
        entries = journal.records;
        pendingRecords = outbox.records;
        // Both reads, de-duplicated by sequence: an unsynced damaged row appears in each, and
        // counting it twice would overstate how much the user lost.
        unreadable = mergeDamaged(journal.damaged, outbox.damaged);
    }
    catch (cause) {
        return err(storageFailure('open', cause));
    }
    if (checkpoint !== undefined && checkpoint.document.map.id !== mapId) {
        // An adapter that returned another map's checkpoint would replay this map's journal over a
        // stranger's document. Refusing is the only safe answer.
        return err(journalError('MAP_CORRUPTED', 'checkpoint belongs to a different map', {
            mapId,
            found: checkpoint.document.map.id,
        }));
    }
    const base = checkpoint?.document ?? options.base;
    if (base === undefined) {
        return err(journalError('MAP_NOT_FOUND', 'no checkpoint stored and no base document supplied', {
            mapId,
            journalledEntries: entries.length,
        }));
    }
    // With no checkpoint the base stands in as one at sequence 0 — replay's contract is "fold in
    // everything after this sequence", and everything after 0 is the whole journal.
    const from = checkpoint === undefined
        ? { document: base, sequence: 0, createdAt: base.map.createdAt }
        : toCheckpoint(checkpoint);
    const replayed = replay(from, entries.map(toEntry));
    const restoration = assessRestoration({
        document: replayed.document,
        checkpointDocument: checkpoint?.document ?? null,
        skipped: replayed.skipped.length,
        unreadable: unreadable.length,
    });
    // The watermark that makes pruning safe. Entries at or below the checkpoint may already have
    // been deleted, so the highest sequence still on disk can be lower than the highest ever
    // assigned — the checkpoint remembers it on their behalf.
    const lastSequence = Math.max(checkpoint?.sequence ?? 0, ...entries.map((record) => record.sequence), ...pendingRecords.map((record) => record.sequence), 0);
    const store = createJournalStore({
        storage,
        mapId,
        state: {
            lastSequence,
            checkpointSequence: checkpoint?.sequence ?? 0,
            checkpointAt: checkpoint?.createdAt ?? null,
            pendingOperations: pendingRecords.length,
            pendingOperationIds: pendingRecords.map((record) => record.operationId),
            recoveryAvailable: restoration.recoveryAvailable,
        },
        ...(options.policy === undefined ? {} : { policy: options.policy }),
    });
    return ok({ ...restoration, store, document: replayed.document, replayed, unreadable });
}
//# sourceMappingURL=open.js.map